// /resources/js/store/CartStore.js

import {defineStore} from 'pinia';
import {getLocalStorage, setLocalStorage, checkDocument, scrollTop} from '@js/helpers.ts';
import {useThemeStore} from "@js/store/ThemeStore.js";
import {ecomAdd, ecomRemove, ecomPurchase, mapProduct} from '@js/ecommerce.js';

// ----- Имена куки и срок хранения -----
const COOKIE_CART = 'cart_metal_v1';   // позиции корзины [{ id, mode, qty }]
const COOKIE_UI = 'cart_ui_metal_v1';// UI карточек (mode/qty) — пишем ТОЛЬКО для товаров, уже добавленных в корзину
const YEAR_IN_MINUTES = 525600;        // 1 год в минутах (для setLocalStorage)

// ----- Вспомогательные округления -----
function roundMoney(v) {
    return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
} // деньги до копеек
function roundTons(v) {
    return Number(Number(v).toFixed(3));
}         // шаг 0.001 т
function roundMeters(v) {
    return Math.max(1, Math.round(Number(v)));
}  // целые метры, минимум 1 м
const roundPieces = roundMeters;

export const useCartStore = defineStore('cartStore', {
    state: () => ({
        // catalog: минимальный снэпшот данных продукта (цены, масса/м и т.п.), чтобы карточка могла считать всё локально
        catalog: {},
        // ui: локальное состояние карточек по id (режим и количество)
        // ВАЖНО: в куки попадут ТОЛЬКО те ui-записи, для которых товар уже есть в корзине
        ui: {},
        // items: собственно корзина — массив строк (id + mode + qty). Эта структура целиком хранится в куках
        items: [],
        loadings: {
            cart_loading: true,
            sending: false,
        },
        isCartClearModalVisible: false,
        cart_products: [],

        form: {
            full_name: '',
            phone: '',
            email: '',
            inn: '',
            comment: '',
            captcha_token: '',

            delivery_type: 'pickup', // 'pickup' | 'inside_mkad' | 'outside_mkad' | 'russia'
            delivery_address: '', // адрес доставки

            vehicle_type: 'truck',
            vehicle: '0_5t',
            payment_type: 'cash', // 'cash' | 'card' | 'invoice'
        },
        cart_form_successfully_sent: false,
        form_errors: [],
    }),

    getters: {
        // Возвращает продукт из локального каталога или null
        productOf: (s) => (id) => s.catalog[id] ?? null,
        // Возвращает UI-состояние карточки (если нет — дефолт: метр и qty=1)
        uiOf: (s) => (id) => s.ui[id] ?? {mode: 'm', qty: 1},

        // Шаг и минимальное количество в зависимости от режима (метры / тонны)
        step() {
            return (id) => this.uiOf(id).mode === 't' ? 0.001 : 1;
        },
        minQty() {
            return (id) => this.step(id);
        },

        priceActive() {
            return (id) => {
                const p = this.productOf(id);
                if (!p) return 0;
                const mode = this.uiOf(id).mode;
                switch (mode) {
                    case 'm':
                        return p.price_per_meter || 0;
                    case 't':
                        return p.price_per_ton || 0;
                    case 'pc':
                        return p.price_per_piece || 0;
                    default:
                        return 0;
                }
            };
        },
        priceOld() {
            return (id) => {
                const p = this.productOf(id);
                if (!p) return null;
                const mode = this.uiOf(id).mode;
                switch (mode) {
                    case 'm':
                        return p.old_price_per_meter ?? null;
                    case 't':
                        return p.old_price_per_ton ?? null;
                    case 'pc':
                        return p.old_price_per_piece ?? null;
                    default:
                        return null;
                }
            };
        },
        hasDiscount() {
            return (id) => {
                const o = this.priceOld(id), c = this.priceActive(id);
                return !!(o && o > c);
            };
        },
        discountPercent() {
            return (id) => this.hasDiscount(id)
                ? Math.round((1 - this.priceActive(id) / this.priceOld(id)) * 100)
                : null;
        },

        priceRows() {
            return (id) => {
                const p = this.productOf(id) ?? {};
                return [
                    {
                        label: '/ м', price: p.price_per_meter || 0, old: p.old_price_per_meter ?? null,
                        discount: (p.old_price_per_meter || 0) > (p.price_per_meter || 0)
                            ? Math.round((1 - (p.price_per_meter || 0) / (p.old_price_per_meter || 1)) * 100) : null
                    },
                    {
                        label: '/ т', price: p.price_per_ton || 0, old: p.old_price_per_ton ?? null,
                        discount: (p.old_price_per_ton || 0) > (p.price_per_ton || 0)
                            ? Math.round((1 - (p.price_per_ton || 0) / (p.old_price_per_ton || 1)) * 100) : null
                    },
                    {
                        label: '/ шт', price: p.price_per_piece || 0, old: p.old_price_per_piece ?? null,
                        discount: (p.old_price_per_piece || 0) > (p.price_per_piece || 0)
                            ? Math.round((1 - (p.price_per_piece || 0) / (p.old_price_per_piece || 1)) * 100) : null
                    },
                ];
            };
        },

        // Масса погонного метра (кг/м)
        massPerMeter() {
            return (id) => Number(this.productOf(id)?.mass_per_meter || 0);
        },

        weightKg() {
            return (id) => {
                const {mode, qty} = this.uiOf(id);
                const mpm = this.massPerMeter(id);
                if (mode === 'm') return Number((roundMeters(qty) * mpm).toFixed(3));
                if (mode === 't') return Number((roundTons(qty) * 1000).toFixed(3));
                if (mode === 'pc') return Number((roundMeters(qty) * mpm).toFixed(3));
                // для штук нет универсального веса -> вернём null
                return null;
            };
        },
        lengthM() {
            return (id) => {
                const {mode, qty} = this.uiOf(id);
                const mpm = this.massPerMeter(id);
                if (mode === 'm') return roundMeters(qty);
                if (mode === 't') {
                    if (!mpm) return null;
                    return Math.max(1, Math.round((roundTons(qty) * 1000) / mpm));
                }
                // для штук длина неприменима
                return null;
            };
        },
        // Сумма (₽) по текущему UI-состоянию (цена активного режима * количество)
        totalPrice() {
            return (id) => roundMoney(this.priceActive(id) * Number(this.uiOf(id).qty || 0));
        },
        // Отображаемая единица измерения в UI
        unitLabel() {
            return (id) => (this.uiOf(id).mode === 'm' ? 'м' : (this.uiOf(id).mode === 't' ? 'т' : 'шт'));
        },
        // Доступность товара
        isAvailable() {
            return (id) => !!this.productOf(id)?.available;
        },

        // Поиск строки в корзине по id и режиму
        findItem: (s) => (id, mode) => s.items.find(i => i.id === id && i.mode === mode),
        // Есть ли товар в корзине в текущем режиме (для зелёной кнопки «Перейти в корзину»)
        isInCartExact() {
            return (id) => !!this.findItem(id, this.uiOf(id).mode);
        },
        // Есть ли товар в корзине в любом режиме (м/т)
        isInCartAny() {
            return (id) => !!this.findItem(id, 'm') || !!this.findItem(id, 't') || !!this.findItem(id, 'pc');
        },
        // Можно ли добавить в корзину (доступен и есть цена)
        canAddToCart() {
            return (id) => this.isAvailable(id) && this.priceActive(id) > 0;
        },

        // Сумма всех строк корзины (без доставки)
        subTotal(state) {
            return roundMoney(state.items.reduce((sum, it) => {
                const p = this.productOf(it.id);
                if (!p) return sum;
                let price = 0;
                if (it.mode === 'm') price = p.price_per_meter || 0;
                else if (it.mode === 't') price = p.price_per_ton || 0;
                else if (it.mode === 'pc') price = p.price_per_piece || 0;
                return sum + price * Number(it.qty);
            }, 0));
        },
        weightTotal(state) {
            const sumKg = state.items.reduce((sum, it) => {
                const p = this.productOf(it.id);
                if (!p) return sum;
                const mpm = Number(p.mass_per_meter || 0);
                let kg = 0;
                if (it.mode === 't') kg = roundTons(it.qty) * 1000;
                else if (it.mode === 'm') kg = roundMeters(it.qty) * mpm;
                else if (it.mode === 'pc') {
                    const massPerPiece =
                        (p.mass_per_piece != null ? Number(p.mass_per_piece) : null) ??
                        (p.piece_length_m != null ? Number(p.piece_length_m) * mpm : null) ??
                        mpm;

                    kg = roundPieces(it.qty) * Number(massPerPiece || 0);
                }

                return sum + (Number.isFinite(kg) ? kg : 0);
            }, 0);

            return Number(sumKg.toFixed(3));
        },
    },

    actions: {
        // ===== helpers: e-commerce =====
        _mapForEcom(id, qty, list = 'Cart') {
            const p = this.productOf(id);
            if (!p) return null;
            const mp = mapProduct(p, {list, position: 1, qty: Number(qty || 1)});
            // проставим variant по текущей единице
            const u = this.unitLabel(id); // 'м' | 'т' | 'шт'
            if (u) mp.variant = u;
            return mp;
        },
        _trackCartDelta(id, mode, prevQty, nextQty) {
            // отправляем только если товар есть в каталоге и был уже в корзине
            const p = this.productOf(id);
            if (!p || prevQty == null || nextQty == null) return;

            const diff = Number(nextQty) - Number(prevQty);
            if (diff === 0) return;

            const qtyAbs = Math.abs(diff);
            const mp = this._mapForEcom(id, qtyAbs, 'Cart');
            if (!mp) return;

            try {
                if (diff > 0) ecomAdd(mp);
                else ecomRemove(mp);
            } catch {
            }
        },

        // ===== INIT =====
        hydrate() {
            // Поднимаем корзину и UI из куков
            this.loadCart();
            this.loadUI();
            // Сразу чистим UI от лишнего: в куках держим UI только для товаров, реально лежащих в корзине
            this._persistUIFiltered();
        },

        // Регистрируем продукт (карточка вызывает это в setup/mounted).
        // ВАЖНО: дефолтный ui записывается в память, но НЕ пишется в куки, пока товар не добавят в корзину.
        registerCatalogProduct(product, defaultMode = 'm') {
            const id = Number(product.id);
            this.catalog[id] = {
                id: product.id,
                title: product.title,
                slug: product.slug,
                category_slug: product.category_slug,

                // цены
                price_per_meter: product.price_per_meter,
                price_per_ton: product.price_per_ton,
                price_per_piece: product.price_per_piece,

                // старые цены (если есть)
                old_price_per_meter: product.old_price_per_meter ?? null,
                old_price_per_ton: product.old_price_per_ton ?? null,
                old_price_per_piece: product.old_price_per_piece ?? null,

                mass_per_meter: product.mass_per_meter,
                available: !!product.available,
                images: product.images ?? null,
            };

            // дефолтный UI (m|t|pc)
            const allowed = new Set(['m', 't', 'pc']);
            const initialMode = allowed.has(defaultMode) ? defaultMode : 'm';
            if (!this.ui[id]) {
                this.ui[id] = {mode: initialMode, qty: initialMode === 't' ? 0.001 : 1};
            }
        },

        // ===== UI (в куки только если товар уже в корзине) =====
        loadUI() {
            // Читаем UI из куки (может содержать только подмножество ui для товаров из корзины)
            const raw = getLocalStorage(COOKIE_UI) || '{}';
            try {
                const obj = JSON.parse(raw);
                this.ui = (obj && typeof obj === 'object') ? obj : {};
            } catch {
                this.ui = {};
            }
        },
        _persistUIFiltered() {
            // Сохраняем в куки только ui тех id, которые реально присутствуют в корзине (items)
            const tracked = new Set(this.items.map(i => i.id));
            const toSave = {};
            for (const [k, v] of Object.entries(this.ui)) {
                const id = Number(k);
                if (tracked.has(id)) toSave[k] = v;
            }
            setLocalStorage(COOKIE_UI, JSON.stringify(toSave), YEAR_IN_MINUTES);
        },

        _syncCartLineIfExists(id) {
            // Если товар уже был добавлен в корзину в текущем режиме — обновляем его количество из UI
            const {mode, qty} = this.uiOf(id);
            const line = this.findItem(id, mode);
            if (!line) return;
            const prev = line.qty;

            // нормализуем новое значение
            if (mode === 't') line.qty = roundTons(qty);
            else line.qty = roundMeters(qty);

            // e-commerce: отправляем разницу
            this._trackCartDelta(id, mode, prev, line.qty);

            if (line.qty <= 0) this.remove(id, mode);
            this.persistCart();
            this._persistUIFiltered();
        },

        setMode(id, next) {
            const allowed = new Set(['m', 't', 'pc']);
            const prev = this.uiOf(id).mode;
            if (!allowed.has(next) || prev === next) return;

            const ui = this.ui[id] ?? {mode: 'm', qty: 1};
            ui.mode = next;
            ui.qty = (next === 't') ? roundTons(Math.max(0.001, ui.qty)) : roundMeters(Math.max(1, ui.qty));
            this.ui[id] = ui;

            const oldLine = this.findItem(id, prev);
            if (oldLine) {
                // e-com: remove старой строки
                this._trackCartDelta(id, prev, oldLine.qty, 0);

                // перенос строки в новый режим
                this.items = this.items.filter(i => !(i.id === id && i.mode === prev));
                const newQty = (next === 't') ? roundTons(ui.qty) : roundMeters(ui.qty);
                const existingNew = this.findItem(id, next);
                if (existingNew) {
                    const before = existingNew.qty;
                    existingNew.qty = newQty;
                    // e-com: delta в новом режиме
                    this._trackCartDelta(id, next, before, existingNew.qty);
                } else {
                    this.items.push({id, mode: next, qty: newQty});
                    // e-com: add новой строки
                    this._trackCartDelta(id, next, 0, newQty);
                }
                this.persistCart();
                this._persistUIFiltered();
            }
        },

        incQty(id) {
            const ui = this.ui[id];
            if (!ui) return;
            const line = this.findItem(id, ui.mode); // только если уже в корзине — трекаем
            const prev = line ? line.qty : null;

            ui.qty = (ui.mode === 't') ? roundTons(ui.qty + 0.001) : roundMeters(ui.qty + 1);
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id, ui.mode)?.qty ?? null;
                this._trackCartDelta(id, ui.mode, prev, next);
            }
        },
        decQty(id) {
            const ui = this.ui[id];
            if (!ui) return;
            const min = (ui.mode === 't') ? 0.001 : 1;
            const line = this.findItem(id, ui.mode);
            const prev = line ? line.qty : null;

            ui.qty = (ui.mode === 't')
                ? roundTons(Math.max(min, ui.qty - 0.001))
                : roundMeters(Math.max(min, ui.qty - 1));
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id, ui.mode)?.qty ?? null;
                this._trackCartDelta(id, ui.mode, prev, next);
            }
        },
        setQty(id, v) {
            const ui = this.ui[id];
            if (!ui) return;
            const num = Number(v);
            if (Number.isNaN(num)) return;

            const min = (ui.mode === 't') ? 0.001 : 1;
            const line = this.findItem(id, ui.mode);
            const prev = line ? line.qty : null;

            ui.qty = (ui.mode === 't') ? roundTons(Math.max(min, num)) : roundMeters(Math.max(min, num));
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id, ui.mode)?.qty ?? null;
                this._trackCartDelta(id, ui.mode, prev, next);
            }
        },
        setQtyFromInput(id, raw) {
            // Поддержка ввода с запятой — конвертируем в точку и вызываем setQty
            this.setQty(id, String(raw).replace(',', '.'));
        },

        // ===== CART =====
        loadCart() {
            const raw = getLocalStorage(COOKIE_CART) || '[]';
            try {
                const arr = JSON.parse(raw);
                this.items = Array.isArray(arr)
                    ? arr
                        .filter(r => r && (r.mode === 'm' || r.mode === 't' || r.mode === 'pc'))
                        .map(r => ({
                            id: Number(r.id),
                            mode: r.mode,
                            qty: r.mode === 'm' ? roundMeters(r.qty)
                                : r.mode === 't' ? roundTons(r.qty)
                                    : /* pc */         roundPieces(r.qty)
                        }))
                    : [];
            } catch {
                this.items = [];
            }
        },

        getCartData() {
            this.loadings.cart_loading = true
            axios.post('/axios/get-products-by-ids', {ids: this.items.map(i => i.id) ?? []})
                .then(response => {
                    this.cart_products = response.data.products ?? [];
                    this.loadings.cart_loading = false;
                })
                .catch(() => {
                    this.loadings.cart_loading = false;
                });
        },

        persistCart() {
            setLocalStorage(COOKIE_CART, JSON.stringify(this.items), YEAR_IN_MINUTES);
        },

        addToCartFromCard(id) {
            id = Number(id);
            if (!this.canAddToCart(id)) return;

            const {mode, qty} = this.uiOf(id);
            const normalizedQty = (mode === 't') ? roundTons(qty) : roundMeters(qty);

            const existing = this.findItem(id, mode);
            if (existing) {
                const prev = existing.qty;
                existing.qty = (mode === 't')
                    ? roundTons(existing.qty + normalizedQty)
                    : roundMeters(existing.qty + normalizedQty);
                // e-com: add ровно на добавленную дельту
                const mp = this._mapForEcom(id, (existing.qty - prev), 'Cart');
                if (mp) {
                    try {
                        ecomAdd(mp);
                    } catch {
                    }
                }
            } else {
                this.items.push({id, mode, qty: normalizedQty});
                // e-com: add новой строки
                const mp = this._mapForEcom(id, normalizedQty, 'Cart');
                if (mp) {
                    try {
                        ecomAdd(mp);
                    } catch {
                    }
                }
            }

            this.persistCart();
            this._persistUIFiltered();

            const themeStore = useThemeStore();
            themeStore.addToast("Добавлено в корзину", "success", {route_name: 'cart'}, 5000);
        },
        updateItemQty(id, mode, qty) {
            const it = this.findItem(Number(id), mode);
            if (!it) return;
            const prev = it.qty;
            it.qty = (mode === 't') ? roundTons(qty) : roundMeters(qty);
            if (it.qty <= 0) {
                // e-com: remove всей позиции
                const mp = this._mapForEcom(Number(id), prev, 'Cart');
                if (mp) {
                    try {
                        ecomRemove(mp);
                    } catch {
                    }
                }
                this.remove(id, mode);
                return;
            }
            // e-com: delta
            this._trackCartDelta(Number(id), mode, prev, it.qty);
            this.persistCart();
            this._persistUIFiltered();
        },

        remove(id, mode) {
            // найдём удаляемую строку, чтобы знать qty для e-commerce
            const it = this.findItem(Number(id), mode);
            if (it) {
                const mp = this._mapForEcom(Number(id), it.qty, 'Cart');
                if (mp) {
                    try {
                        ecomRemove(mp);
                    } catch {
                    }
                }
            }
            this.items = this.items.filter(i => !(i.id === Number(id) && i.mode === mode));
            this.persistCart();
            // Если товара больше не осталось ни в одном режиме — убираем его ui из куки
            const stillHas = this.items.some(i => i.id === Number(id));
            if (!stillHas) {
                delete this.ui[id];
            }
            this._persistUIFiltered();
            this.getCartData()
        },

        clear() {
            // e-com: remove для всех позиций (поочерёдно)
            try {
                for (const it of this.items) {
                    const mp = this._mapForEcom(it.id, it.qty, 'Cart');
                    if (mp) ecomRemove(mp);
                }
            } catch {
            }

            this.items = [];
            this.persistCart();
            // И соответствующая очистка UI в куках (фильтр сделает пустой объект)
            this._persistUIFiltered();
            this.getCartData()
        },

        sendForm() {
            if (!checkDocument()) return;

            this.form_errors = []
            this.loadings.sending = false
            this.cart_form_successfully_sent = false
            this.form.captcha_token = ''

            const captchaId = 'smart-captcha-container-cart-form'

            // 1. Удаление старого контейнера + destroy капчи
            const existing = document.getElementById(captchaId)
            if (existing) {
                try {
                    window.smartCaptcha.destroy(existing.dataset.widgetId)
                } catch (e) {
                }
                existing.remove()
            }

            // 2. Создание нового скрытого контейнера
            const container = document.createElement('div')
            container.id = captchaId
            container.style.position = 'absolute'
            container.style.zIndex = '-1'
            container.style.left = '-9999px'
            container.style.top = '-9999px'
            document.body.appendChild(container)

            let tokenReceived = false

            // 3. Рендер капчи
            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true
                    this.loadings.sending = true
                    this.form.captcha_token = token

                    axios.post('/axios/form/send-lead', {
                        bitrix_title: 'Оформление заказа на сайте',
                        ...this.form,
                        cart_products: this.items,
                    }).then(response => {
                        scrollTop()
                        this.loadings.sending = false
                        this.cart_form_successfully_sent = true

                        // === e-commerce: purchase (если бэк вернул данные заказа)
                        try {
                            const orderId = response?.data?.order_id ?? null;
                            const revenue = typeof response?.data?.revenue === 'number' ? response.data.revenue : undefined;
                            if (orderId) {
                                const products = this.items.map((it, idx) => {
                                    const p = this.productOf(it.id);
                                    const mp = mapProduct(p, {
                                        list: 'Purchase',
                                        position: idx + 1,
                                        qty: Number(it.qty || 1)
                                    });
                                    // variant по режиму позиции, а не по ui (важно)
                                    mp.variant = (it.mode === 't') ? 'т' : (it.mode === 'm' ? 'м' : 'шт');
                                    return mp;
                                });
                                ecomPurchase({orderId, revenue, products});
                            }
                        } catch {
                        }

                        // важно: чистим корзину ПОСЛЕ попытки purchase
                        this.clear()

                        // ym(102081669, 'reachGoal', 'order_form_submit')
                        try {
                            window.smartCaptcha.destroy(widgetId)
                        } catch (e) {
                        }
                    }).catch(error => {
                        this.loadings.sending = false
                        if (error.response?.status === 422) {
                            this.form_errors = error.response.data.errors
                        }
                    })
                }
            })

            // 4. Привязка widgetId к контейнеру
            document.getElementById(captchaId).dataset.widgetId = widgetId

            // 5. Отслеживание ручного закрытия
            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.loadings.sending = false
                    this.form_errors = {
                        captcha_token: ['Вы закрыли капчу. Повторите попытку.']
                    }
                    try {
                        window.smartCaptcha.reset(widgetId)
                    } catch (e) {
                    }
                }
            })

            // 6. Запуск капчи
            setTimeout(() => {
                try {
                    window.smartCaptcha.execute(widgetId)
                } catch (e) {
                    this.loadings.sending = false
                    this.form_errors = {
                        captcha_token: ['Не удалось запустить капчу. Попробуйте снова.']
                    }
                }
            }, 100)
        },
        setVehicle(id) {
            this.form.vehicle = id
        }
    }
});
